import { Component, Inject, Injector, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { CommonConstants } from 'src/app-core/constants/common-constants';
import { CommonService } from 'src/app-core/services/common.service';
import { AddAddressComponent } from '../add-address/add-address.component';
import { ContactFormDialogComponent } from '../add-contact/contact-form-dialog/contact-form-dialog.component';
import { AppComponentBase } from '../app-component-base';
import { CommonDataModel } from '../common-data-model';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AddQuickContactComponentForm } from '../quick-contact/add-quick-contact-form.component';

@Component({
    selector: 'app-order-preview-dialog',
    templateUrl: './order-preview-dialog.component.html',
    styleUrls: ['./order-preview-dialog.component.scss']
})
export class OrderPreviewDialogComponent extends AppComponentBase implements OnInit {

    @ViewChild(TooltipDirective, { static: true }) public tooltipDir: TooltipDirective;
    commonDataModel = new CommonDataModel();
    isFormLoading = false;
    dummy: any;
    orderPreviewData: any;
    orderId: any;
    dialogData: any;
    gridItems: any = {
        data: [],
        total: 0
    };
    specialInstructions: any;
    minDate = moment().format();
    jobId: any;
    shipFromStartTime: any;
    shipFromEndTime: any;
    shipToStartTime: any;
    shipToEndTime: any;
    minOperationEndTime: any;
    address = 'Address';
    phone = 'Phone';
    defaultStartTime: any = moment().set({ hour: 8, minute: 0, second: 0, millisecond: 0 }).format();
    defaultEndTime: any = moment().set({ hour: 17, minute: 0, second: 0, millisecond: 0 }).format();

    constructor(
        injector: Injector,
        private toastr: ToastrService,
        public dialogRef: MatDialogRef<OrderPreviewDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private commonService: CommonService,
        private dialog: MatDialog,
        private router: Router,
        private activeRoute: ActivatedRoute
    ) {
        super(injector);
    }

    ngOnInit() {
        this.jobId = this.activeRoute.snapshot.queryParams.jobId;
        this.dialogData = this.data;
        this.orderId = this.dialogData.orderId;
        this.onLoadItems();
    }

    onLoadItems() {
        this.getShipmentsData();
        this.getInitialDataForFreightShopper();
    }

    getShipmentsData() {
        this.isFormLoading = true;
        const query = `?OrderId=${this.orderId}`;
        this.commonService.getFinalDimsRouting(query)
            .pipe(finalize(() => (this.isFormLoading = false))).subscribe(response => {
                this.isFormLoading = false;
                if (response.isSuccess) {
                    this.specialInstructions = response.data;
                    this.gridItems.data = response.data.getFinalBol ? response.data.getFinalBol : [];
                } else {
                    this.toastr.error(
                        response.errors || CommonConstants.appComponent.somethingWenWrongPleaseReTry, CommonConstants.appComponent.error);
                }
            });
    }

    onClose(confirmResult?: object): void {
        this.dialogRef.close(confirmResult);
    }

    getInitialDataForFreightShopper() {
        this.isFormLoading = true;
        const payload = {
            id: this.orderId
        };
        this.commonService.getDataForFreightShopper(payload)
            .pipe(finalize(() => (this.isFormLoading = false)))
            .subscribe((response: any) => {
                if (response.isSuccess) {
                    this.orderPreviewData = response.data;
                    if (this.orderPreviewData.currentQuote) {
                        this.orderPreviewData.currentQuote.pickupDate = response.data.currentQuote ? response.data.currentQuote.pickupDate ? response.data.currentQuote.pickupDate : new Date() : new Date();
                        this.orderPreviewData.currentQuote.shipFromReadyTime = response.data.currentQuote ? response.data.currentQuote.shipFromReadyTime ? moment(response.data.currentQuote.shipFromReadyTime, ['h:m a', 'H:m']).format() : this.defaultStartTime : this.defaultStartTime;
                        this.orderPreviewData.currentQuote.shipFromCloseTime = response.data.currentQuote ? response.data.currentQuote.shipFromCloseTime ? moment(response.data.currentQuote.shipFromCloseTime, ['h:m a', 'H:m']).format() : this.defaultEndTime : this.defaultEndTime;
                        this.orderPreviewData.currentQuote.shipToOpenTime = response.data.currentQuote ? response.data.currentQuote.shipToOpenTime ? moment(response.data.currentQuote.shipToOpenTime, ['h:m a', 'H:m']).format() : this.defaultStartTime : this.defaultStartTime;
                        this.orderPreviewData.currentQuote.shipToCloseTime = response.data.currentQuote ? response.data.currentQuote.shipToCloseTime ? moment(response.data.currentQuote.shipToCloseTime, ['h:m a', 'H:m']).format() : this.defaultEndTime : this.defaultEndTime;
                    } else {
                        this.orderPreviewData.currentQuote = {};
                        this.orderPreviewData.currentQuote.pickupDate = new Date();
                        this.orderPreviewData.currentQuote.shipFromReadyTime = this.defaultStartTime;
                        this.orderPreviewData.currentQuote.shipFromCloseTime = this.defaultEndTime;
                        this.orderPreviewData.currentQuote.shipToOpenTime = this.defaultStartTime;
                        this.orderPreviewData.currentQuote.shipToCloseTime = this.defaultEndTime;
                    }
                }
            });
    }

    onBook() {
        this.dialog.open(ConfirmDialogComponent, {
            panelClass: 'confirm_layout',
            hasBackdrop: false,
            data: {
                message: CommonConstants.orderPreviewDialogComponent.bookingConfirmation,
                cancelLabel: CommonConstants.appComponent.no,
                confirmLabel: CommonConstants.appComponent.yes,
                isShowCrossButton: true,
                titleText: CommonConstants.appComponent.areYouSure,
                isShowTitle: true,
            },
        }).afterClosed()
            .subscribe((response: any) => {
                if (response && response.confirmResult) {
                    this.isFormLoading = true;
                    this.orderPreviewData.shipper.readyTime = this.convertTimeToTimeString(this.commonService.convertedDateTimeFormat(this.orderPreviewData.currentQuote.shipFromReadyTime));
                    this.orderPreviewData.shipper.closeTime = this.convertTimeToTimeString(this.commonService.convertedDateTimeFormat(this.orderPreviewData.currentQuote.shipFromCloseTime));
                    this.orderPreviewData.consignee.readyTime = this.convertTimeToTimeString(this.commonService.convertedDateTimeFormat(this.orderPreviewData.currentQuote.shipToOpenTime));
                    this.orderPreviewData.consignee.closeTime = this.convertTimeToTimeString(this.commonService.convertedDateTimeFormat(this.orderPreviewData.currentQuote.shipToCloseTime));
                    this.orderPreviewData.shipper.accountName = this.orderPreviewData.shipper.isQuickContact ? this.orderPreviewData.account.accountName : this.orderPreviewData.shipper.accountName;
                    this.orderPreviewData.consignee.accountName = this.orderPreviewData.consignee.accountName ? this.orderPreviewData.consignee.accountName : this.orderPreviewData.consignee.contactName;
                    this.orderPreviewData.shipper.referenceNumber = this.specialInstructions.originReferenceNumber;
                    this.orderPreviewData.shipper.instructions = this.specialInstructions.originInstructions;
                    this.orderPreviewData.consignee.referenceNumber = this.specialInstructions.destReferenceNumber;
                    this.orderPreviewData.consignee.instructions = this.specialInstructions.destInstructions;
                    const payload = {
                        orderId: this.orderPreviewData.id,
                        shipmentId: this.orderPreviewData.currentQuote ? this.orderPreviewData.currentQuote.quoteId : null,
                        rateId: this.orderPreviewData.currentQuote ? this.orderPreviewData.currentQuote.rateId : null,
                        shedulePickup: true,
                        origin: this.orderPreviewData.shipper ? this.orderPreviewData.shipper : null,
                        destination: this.orderPreviewData.consignee ? this.orderPreviewData.consignee : null,
                        payer: this.orderPreviewData.payer ? this.orderPreviewData.payer : null,
                        pickupDate: moment(this.orderPreviewData.currentQuote.pickupDate).utcOffset(0, true).format(),
                    };
                    this.commonService.freightViewBooking(payload)
                        .pipe(finalize(() => (this.isFormLoading = false)))
                        .subscribe((freightViewResponse: any) => {
                            this.isFormLoading = false;
                            if (freightViewResponse.isSuccess) {
                                this.onClose({ confirmResult: true });
                                this.toastr.success(freightViewResponse.errors || CommonConstants.appComponent.savedSuccessfully);
                                if (freightViewResponse.errors === CommonConstants.appComponent.finalDimsBookingSuccessMessage
                                    || freightViewResponse.errors === CommonConstants.appComponent.finalDimsBookingSuccessConvertLeadMessage) {
                                    this.router.navigateByUrl(`app/jobs/job-details/final-dims-routing?jobId=${this.jobId}`);
                                }
                            } else {
                                this.onClose();
                                this.toastr.error(freightViewResponse.errors);
                                if (freightViewResponse.data.isUpdateAddress) {
                                    if (this.orderPreviewData.shipper.isShipperAddress && !this.orderPreviewData.shipper.addressLine1) {
                                        if (this.orderPreviewData.shipper.isQuickContact) {
                                            this.onOpenQuickContact(this.orderPreviewData.shipper, this.address);
                                        } else {
                                            this.onOpenAddressDialog(this.orderPreviewData.shipper);
                                        }
                                    } else {
                                        if (this.orderPreviewData.consignee.isQuickContact) {
                                            this.onOpenQuickContact(this.orderPreviewData.consignee, this.address);
                                        } else {
                                            this.onOpenAddressDialog(this.orderPreviewData.consignee);
                                        }
                                    }
                                } else if (freightViewResponse.data.isUpdateContact) {
                                    if (this.orderPreviewData.shipper.isShipperAddress && !this.orderPreviewData.shipper.phoneNumber) {
                                        if (this.orderPreviewData.shipper.isQuickContact) {
                                            this.onOpenQuickContact(this.orderPreviewData.shipper, this.phone);
                                        } else {
                                            this.onOpenContactDialog(this.orderPreviewData.shipper);
                                        }
                                    } else {
                                        if (this.orderPreviewData.consignee.isQuickContact) {
                                            this.onOpenQuickContact(this.orderPreviewData.consignee, this.phone);
                                        } else {
                                            this.onOpenContactDialog(this.orderPreviewData.consignee);
                                        }
                                    }
                                }
                            }
                        });
                }
            });
    }

    onShowTooltip(e: any): void {
        if (e.target.offsetWidth < e.target.scrollWidth) {
            this.tooltipDir.toggle({ nativeElement: e.target });
        } else {
            this.tooltipDir.hide();
        }
    }

    onOpenQuickContact(data: any, from: string): void {
        this.dialog
            .open(AddQuickContactComponentForm, {
                position: {
                    right: '0',
                },
                panelClass: 'small_layout',
                hasBackdrop: false,
                autoFocus: false,
                data: {
                    contactId: data.contactId,
                    isAddress: from === this.address ? true : false,
                    isPhoneNumber: from === this.phone ? true : false,
                    isBooking: true,
                    orderId: this.orderPreviewData.id
                }
            })
            .afterClosed()
            .subscribe((response: any) => {
                if (response) {
                }
            });
    }

    onOpenContactDialog(data: any): void {
        this.dialog
            .open(ContactFormDialogComponent, {
                position: {
                    right: '0',
                },
                panelClass: 'small_layout',
                hasBackdrop: false,
                data: {
                    contactId: data.contactId,
                    isEdit: true,
                    accountName: data.accountName,
                    accountId: data.accountId,
                    accountTypeId: data.accountTypeId,
                    isBooking: true,
                    orderId: this.orderPreviewData.id
                },
            })
            .afterClosed()
            .subscribe((response: any) => {
                if (response) {
                }
            });
    }

    onOpenAddressDialog(data: any): void {
        this.dialog.open(AddAddressComponent, {
            autoFocus: false,
            position: {
                right: '0',
            },
            panelClass: 'small_layout',
            hasBackdrop: false,
            data: {
                accountId: data.accountId,
                addressId: data.addressId,
                isBooking: true,
                orderId: this.orderPreviewData.id
            },
        }).afterClosed()
            .subscribe((response: any) => {
                if (response) {
                }
            });
    }

    convertTimeToTimeString(time: any) {
        const exactTime = new Date(time);
        const hours = exactTime.getHours();
        const minutes = this.commonService.addZeroToMinutes(exactTime.getMinutes());
        const timeString = this.commonService.getExactHours(hours, minutes);
        return timeString;
    }

}
